/**
 * Clock formatting and the `mm:ss` duration input, shared by the player chrome
 * (`packages/ui`) and the creator's lesson editor (`apps/creator`).
 *
 * One parser, one formatter — a duration typed into the editor and a duration
 * rendered under the scrubber must agree, or "12:34" saves as 1234 seconds.
 */

/**
 * Seconds → `m:ss`, or `h:mm:ss` past an hour. Never a leading `0:` hour and
 * never a negative or `NaN` clock: anything unusable reads as `0:00`, which is
 * what an unknown duration should look like on a scrubber.
 */
export function formatClock(seconds: number): string {
  const total = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const mm = hours > 0 ? String(minutes).padStart(2, '0') : String(minutes);
  return `${hours > 0 ? `${hours}:` : ''}${mm}:${String(secs).padStart(2, '0')}`;
}

/**
 * A typed duration → seconds.
 *
 * Three outcomes, and the caller must handle all three:
 *   - `number`  — a duration to save.
 *   - `null`    — the field was CLEARED. The creator editor sends an explicit
 *                 null (NULLing the column); a blank box is not an error.
 *   - `'invalid'` — unparseable. Show a hint and block Save; never coerce it to
 *                 0, which would silently wipe a real duration.
 *
 * Accepts `90`, `1:30` and `1:02:03`. Every field after the first is a
 * sexagesimal pair, so `1:99` is invalid rather than quietly 159 seconds.
 * Range (the API's 1..86400) is the caller's business, not the parser's.
 */
export function parseDurationInput(text: string): number | null | 'invalid' {
  if (typeof text !== 'string') return 'invalid';
  const trimmed = text.trim();
  if (trimmed === '') return null;

  const parts = trimmed.split(':');
  if (parts.length > 3) return 'invalid';

  const nums: number[] = [];
  for (const part of parts) {
    // Digits only: this is what rejects `-5`, `1.5`, `abc` and an empty field.
    if (!/^\d+$/.test(part)) return 'invalid';
    nums.push(Number(part));
  }

  const first = nums[0];
  if (first === undefined) return 'invalid';
  if (nums.length === 1) return first;

  let total = first;
  for (let i = 1; i < nums.length; i += 1) {
    const part = nums[i];
    if (part === undefined || part > 59) return 'invalid';
    total = total * 60 + part;
  }
  return total;
}
